import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { ControlValueAccessor, ValidationErrors, Validator } from '@angular/forms';
import { Subject } from 'rxjs';

@Component({
    template: '',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export abstract class CustomFormControlComponent<T = any> implements ControlValueAccessor, OnDestroy, Validator {
    get value(): T {
        return this._value;
    }

    set value(value: T) {
        this._value = value;
        this._onChange.next(value);
        this._onValidationChange.next();
    }

    protected _value: T; // Main Value
    protected _isDisabled = false; // State variables
    protected _onChange = new Subject<T>(); // State subjects
    protected _onTouched = new Subject<void>();
    protected _onValidationChange = new Subject<void>();

    ngOnDestroy(): void {
        this._onChange.complete();
        this._onTouched.complete();
        this._onValidationChange.complete();
    }

    /**
     * Writes the value to the form field
     * @param value value to write
     */
    writeValue(value: T): void {
        this.value = value;
    }

    /**
     * Registers a function to be executed when the value changes
     * @param fn Function to execute on value change
     */
    registerOnChange(fn: (value: T) => void): void {
        this._onChange.subscribe(fn);
    }

    /**
     * Registers a function to be executed when the input is touched
     * @param fn Function to execute on touch
     */
    registerOnTouched(fn: () => void): void {
        this._onTouched.subscribe(fn);
    }

    /**
     * Sets the disabled state of the control
     * @param isDisabled Whether control is disabled
     */
    setDisabledState(isDisabled: boolean): void {
        this._isDisabled = isDisabled;
    }

    /**
     * Validates the form control
     * @returns validation errors
     */
    validate(): ValidationErrors | null {
        // eslint-disable-next-line no-null/no-null
        return null;
    }

    /**
     * Registers a function to be executed on validator change
     * @param fn Function to execute on validator change
     */
    registerOnValidatorChange?(fn: () => void): void {
        this._onValidationChange.subscribe(fn);
    }
}
